import React, { useState, useEffect, useMemo } from 'react'
import { LicenseCard } from '../../components/LicenseCard'
import Button from '../../components/Button'
import PurchaseDetailsModal from '../../components/PurchaseDetailsModal'
import { useGetLicenseProductsListApiMutation, useGetTriningCoursesListApiMutation } from '../../redux/api/getLicenseProductsApi'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useLocation } from 'react-router-dom'
import { formatAmount } from '../../utilities/helpers'
import { commonLabelName } from '../../utilities/commonConstants'
import { StudentLicensesLabel, TrainingCoursesLabel } from '../../utilities/pageConstants'
/**
 * Interface representing a License.
 */
interface License {
    ProductCode: string
    Name: string
    term: string
    imageSrc: string
    UnitPrice: string | 'Free'
    type: 'priced' | 'free'
    UnitPricewithMaintenance?: string
}

interface StudentLicensesProps {
    selectedLicenses: Set<string> | any
    setSelectedLicenses: React.Dispatch<React.SetStateAction<Set<string>>> | any
    quantities: Record<string, number> | any
    setQuantities: React.Dispatch<React.SetStateAction<Record<string, number>>> | any
    setMainTab: React.Dispatch<React.SetStateAction<'productDownloads' | 'myLicenses' | 'commercial' | 'academic' | 'training'>> | any
    selectedLicensesDetails:
        | {
              ProductCode: string
              ProductName: string
              Quantity: number
              Price: string
              UiPrice: string
              Term: string
          }[]
        | any
    setSelectedLicensesDetails:
        | React.Dispatch<
              React.SetStateAction<
                  {
                      ProductCode: string
                      ProductName: string
                      Quantity: number
                      Price: string
                      UiPrice: string
                      Term: string
                  }[]
              >
          >
        | any
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>> | any
    isModalOpen: boolean | any
    onLoadingChange?: (isLoading: boolean) => void
}
/**
 * Component for displaying and selecting student licenses.
 * Allows users to choose between priced and free licenses,
 * select quantities, and proceed with the purchase.
 */
const StudentLicenses: React.FC<StudentLicensesProps> = ({
    selectedLicenses,
    setSelectedLicenses,
    quantities,
    setQuantities,
    selectedLicensesDetails,
    setSelectedLicensesDetails,
    setModalOpen,
    isModalOpen,
    setMainTab,
    onLoadingChange
}) => {
    const location = useLocation()
    const [selectedMainTab, setSelectedMainTab] = useState<'studentLicenses' | 'trainingCourses'>('studentLicenses')
    const [selectedTab, setSelectedTab] = useState<'priced' | 'free'>('priced')
    // const [selectedLicenses, setSelectedLicenses] = useState<Set<string>>(new Set())
    // const [quantities, setQuantities] = useState<Record<string, number>>({})
    // const [isModalOpen, setModalOpen] = useState(false)
    const [licenses, setLicenses] = useState<License[]>([])
    const [GetLicenseProductsList, { isLoading }] = useGetLicenseProductsListApiMutation()
    const [GetTrainingCoursesList, { isLoading: trainingCoursesLoading }] = useGetTriningCoursesListApiMutation()
    // const [selectedLicensesDetails, setSelectedLicensesDetails] = useState<
    //     { ProductCode: string; ProductName: string; Quantity: number; Price: string; UiPrice: string; Term: string }[]
    // >([])
    const studentLicenses = ['ENVIDLTLA', 'IDLTLA', 'ENVTLA', 'SUPUNL', 'ENVACMTLA']
    const trainingCourses = ['ELNISRS', 'ELNEBG', 'ELNAEG', 'ELNEFUN']

    // Determine the type dynamically based on selected licenses
    const computedType = useMemo(() => {
        const selectedCodes = Array.from(selectedLicenses) as string[]
        const hasStudentLicense = selectedCodes.some((code) => studentLicenses.includes(code))
        const hasTraining = selectedCodes.some((code) => trainingCourses.includes(code))

        if (hasStudentLicense && hasTraining) return 'ACADEMIC,TRAINING'
        if (hasStudentLicense) return 'ACADEMIC'
        if (hasTraining) return 'ACADEMICTRAININGONLY'
        return '' // Default fallback
    }, [selectedLicenses])

    // Notify parent component about loading state changes
    useEffect(() => {
        const isComponentLoading = isLoading || trainingCoursesLoading
        onLoadingChange?.(isComponentLoading)
    }, [isLoading, trainingCoursesLoading, onLoadingChange])

    /**
     * Fetches the list of available licenses / training courses based on the selected tab.
     */
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (selectedMainTab === 'studentLicenses') {
                    // Fetching licenses
                    const response = await GetLicenseProductsList({
                        flow: 'Academic',
                        licenseType: selectedTab === 'priced' ? 'Priced' : 'Free'
                    }).unwrap()
                    setLicenses(response.data)
                } else if (selectedMainTab === 'trainingCourses') {
                    // Fetching training courses
                    const response = await GetTrainingCoursesList({
                        flow: 'Academic',
                        licenseType: 'training'
                    }).unwrap()
                    setLicenses(response.data)
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            }
        }

        fetchData()
    }, [selectedMainTab, selectedTab, GetLicenseProductsList, GetTrainingCoursesList])

    /**
     * Toggles the selection of a license.
     * @param {string} id - The ProductCode of the license.
     * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void}
     */
    const toggleLicense =
        (id: string): ((event: React.ChangeEvent<HTMLInputElement>) => void) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newSelected = new Set<string>(selectedLicenses as Set<string>)

            // Find the license data based on the id
            const license = licenses.find((license) => license.ProductCode === id)

            // Determine the license data to add/remove based on tab selection
            const quantity = quantities[id] || 0
            const licenseDetails = {
                ProductCode: license?.ProductCode || '',
                ProductName: license?.Name || '',
                Quantity: quantity,
                Price:
                    selectedMainTab === 'studentLicenses' && selectedTab === 'free' ? 'Free' : license?.UnitPrice ? license?.UnitPrice : '',
                UiPrice: (selectedMainTab === 'studentLicenses' && license?.UnitPricewithMaintenance) || '',
                Term: selectedTab === 'free' ? '3-month Semester Term License' : '12 Month Term License'
            }

            if (event.target.checked) {
                // Add license to the selected set and add details to the selectedLicensesDetails state
                newSelected.add(id)

                setSelectedLicensesDetails((prev: any) => [...prev, licenseDetails])
            } else {
                // Remove license from the selected set and remove details from selectedLicensesDetails state
                newSelected.delete(id)

                setSelectedLicensesDetails((prev: any[]) => prev.filter((item: { ProductCode: string }) => item.ProductCode !== id))
            }

            // Update the selectedLicenses state with the new set
            setSelectedLicenses(newSelected)
        }
    /**
     * Handles changes in quantity selection for licenses.
     * @param {string} id - The ProductCode of the license.
     * @param {number} newQuantity - The new quantity selected.
     */
    const handleQuantityChange = (id: string, newQuantity: number) => {
        setQuantities((prev: Record<string, number>) => {
            interface UpdatedQuantities {
                [key: string]: number
            }

            const updatedQuantities: UpdatedQuantities = {
                ...prev,
                [id]: newQuantity > 0 ? newQuantity : 0
            }

            // If the quantity is 0, remove the license from selectedLicenses and selectedLicensesDetails
            if (newQuantity === 0) {
                const newSelected: Set<string> = new Set<string>(selectedLicenses as Set<string>)
                newSelected.delete(id) // Remove from selected licenses
                setSelectedLicenses(newSelected) // Update the selectedLicenses state

                // Remove from selectedLicensesDetails state
                setSelectedLicensesDetails(
                    (
                        prevDetails: {
                            ProductCode: string
                            ProductName: string
                            Quantity: number
                            Price: string
                            UiPrice: string
                            Term: string
                        }[]
                    ) => prevDetails.filter((item) => item.ProductCode !== id)
                )
            }

            return updatedQuantities
        })
    }
    /**
     * Renders a list of license cards.
     * @param {License[]} licenses - The list of licenses to render.
     * @returns {JSX.Element} The rendered list of licenses or a "No data found" message.
     */
    const renderLicenses = (licenses: License[]) => {
        return licenses && licenses.length > 0 ? (
            licenses.map((license) => {
                const unitPriceStr = String(license.UnitPrice)
                const displayPrice = unitPriceStr === '0' || unitPriceStr === '' ? '0' : formatAmount(Number(license.UnitPrice))

                return (
                    <LicenseCard
                        key={license?.ProductCode}
                        productCode={license?.ProductCode}
                        title={license?.Name}
                        term={
                            selectedMainTab === 'studentLicenses'
                                ? '12 Month Term License'
                                : selectedMainTab === 'trainingCourses'
                                  ? '2 Months Duration'
                                  : ''
                        }
                        imageSrc={license.imageSrc}
                        price={selectedMainTab === 'studentLicenses' && selectedTab === 'free' ? '' : displayPrice}
                        isSelected={selectedLicenses.has(license?.ProductCode)}
                        onSelect={toggleLicense(license?.ProductCode)}
                        setQuantity={(newQuantity: number) => handleQuantityChange(license?.ProductCode, newQuantity)}
                        quantity={quantities[license?.ProductCode]}
                    />
                )
            })
        ) : (
            <div className='flex justify-center items-center h-48'>
                <span className='text-gray-500'>{commonLabelName?.noDataFound}</span>
            </div>
        )
    }

    return (
        <div className='p-6 max-w-7xl mx-auto border border-gray bg-[#f9f9f9] rounded-lg'>
            <div className='flex justify-between'>
                <div className='flex border-b border-[#e1e1e1] gap-16 mt-4'>
                    {/* <button
                        className={`px-4 py-2 text-lg font-semibold ${
                            selectedMainTab === 'studentLicenses' ? 'text-primary border-b-2 border-primary' : 'text-secondary'
                        }`}
                        onClick={() => setSelectedMainTab('studentLicenses')}
                    >
                        {StudentLicensesLabel?.studentLicenses}
                    </button>
                    <button
                        className={`px-4 py-2 text-lg font-semibold ${
                            selectedMainTab === 'trainingCourses' ? 'text-primary border-b-2 border-primary' : 'text-secondary'
                        }`}
                        onClick={() => setSelectedMainTab('trainingCourses')}
                    >
                        {TrainingCoursesLabel?.trainingCourses}
                    </button> */}
                </div>
            </div>
            {/* <div className='ml-2'>
                <div className='text-secondary text-center font-normal'>
                    <p>Online credit card purchase is available only in the US in USD currency.To order products that</p>
                    <p className='font-normal'>
                        are not shown here, please{' '}
                        <a
                            href='https://www.nv5geospatialsoftware.com/Company/Contact-Us'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary underline font-semibold'
                        >
                            Contact your Sales Representative
                        </a>
                        &nbsp;or&nbsp;
                        <a
                            href='https://www.nv5geospatialsoftware.com/Request-a-Quote'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary underline font-semibold'
                        >
                            Request a Quote
                        </a>
                        .
                    </p>
                    <p>
                        Problems? &nbsp;
                        <a
                            href='mailto:geospatialsupport@nv5.com'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary underline font-semibold'
                        >
                            geospatialsupport@nv5.com
                        </a>
                    </p>
                </div>
            </div>
            {/* Sub-Tabs */}
            {/* {selectedMainTab === 'studentLicenses' && (
                <div className='flex justify-start mt-4'>
                    <div className='flex border border-[#e1e1e1] rounded-lg overflow-hidden w-fit bg-white'>
                        <div
                            className={`px-6 py-2 text-center text-md font-semibold cursor-pointer ${
                                selectedTab === 'priced' ? 'bg-primary text-white' : 'text-secondary'
                            }`}
                            onClick={() => setSelectedTab('priced')}
                        >
                            {StudentLicensesLabel?.pricedLicenses}
                        </div>
                        <div className='w-[1px] bg-gray-300' />
                        <div
                            className={`px-6 py-2 text-center text-md font-semibold cursor-pointer ${
                                selectedTab === 'free' ? 'bg-primary text-white' : 'text-secondary bg-gray-100 hover:bg-gray-200'
                            }`}
                            onClick={() => setSelectedTab('free')}
                        >
                            {StudentLicensesLabel?.freeLicenses}
                        </div>
                    </div>
                </div>
            )} */}

            {/* License Cards */}
            <div className='mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {isLoading || trainingCoursesLoading
                    ? Array.from({ length: 4 }).map((_, index) => (
                          <div key={index} className='bg-gray-200 rounded-lg h-24 p-2 mb-4'>
                              <Skeleton height={100} />
                          </div>
                      ))
                    : renderLicenses(licenses)}
            </div>

            <div className='flex justify-end mt-6'>
                <Button
                    variant='primary'
                    className={`px-6 py-2 bg-primary text-white rounded-md ${
                        selectedLicenses.size === 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={selectedLicenses.size === 0}
                    onClick={() => setModalOpen(true)}
                >
                    {commonLabelName?.proceed}
                </Button>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <PurchaseDetailsModal
                    licenses={selectedLicensesDetails}
                    quantities={quantities}
                    onClose={() => setModalOpen(false)}
                    type={computedType}
                    setMainTab={setMainTab}
                    setSelectedLicenses={setSelectedLicenses}
                    setQuantities={setQuantities}
                    setSelectedLicensesDetails={setSelectedLicensesDetails}
                />
            )}
        </div>
    )
}

export default StudentLicenses
